import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import { deleteProgram, fetchPrograms, previewProgram, saveProgram } from '../../api/ops';
import { Card, Field, Modal } from '../../ui/components';
import { useWidth } from '../../ui/useWidth';
import { useMe } from '../auth/session';
import { useReportScope } from '../reports/ReportScope';
import { levelText } from './GroupsPage';

/** Lighting programs: switch points at a local time or relative to sunset / sunrise, with a level (0 = off). */
export function ProgramsTab() {
  const { t } = useTranslation();
  const me = useMe().data;
  const { clientId } = useReportScope();
  const programs = useQuery({
    queryKey: ['programs', clientId],
    queryFn: () => fetchPrograms(clientId),
    enabled: !!clientId,
  });
  const [editing, setEditing] = useState<api.Program | 'new' | null>(null);
  return (
    <Card
      title={t('groupsPage.programs')}
      bodyClass=""
      actions={
        canAny(me, 'schedule.manage') && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!clientId}
            onClick={() => setEditing('new')}
          >
            <Plus size={15} /> {t('groupsPage.newProgram')}
          </button>
        )
      }
    >
      <div className="card-body muted small" style={{ paddingBottom: 0 }}>
        {t('groupsPage.programsHint')}
      </div>
      <div className="ops-scroll">
        <table className="table table-wrap">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('groupsPage.steps')}</th>
              <th className="right">{t('groupsPage.usedBy')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(programs.data?.items ?? []).map((p) => (
              <tr key={p.id}>
                <td>
                  <strong>{p.name}</strong>
                  {!p.client_id && (
                    <span className="pill" style={{ marginLeft: 6 }}>
                      {t('groupsPage.template')}
                    </span>
                  )}
                  {p.description && <span className="cell-sub">{p.description}</span>}
                </td>
                <td className="small">
                  {p.steps.map((s) => `${stepLabel(s, t)} → ${levelText(s.level, t)}`).join(' · ')}
                </td>
                <td className="num">{p.groups}</td>
                <td className="right">
                  <button type="button" className="btn btn-ghost" onClick={() => setEditing(p)}>
                    {p.can_manage ? t('common.edit') : t('common.details')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && clientId && (
        <ProgramModal
          clientId={clientId}
          program={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function stepLabel(
  s: api.ProgramStep,
  t: (k: 'groupsPage.at.sunset' | 'groupsPage.at.sunrise') => string,
): string {
  if (s.at.type === 'time') return s.at.time;
  const sign = s.at.offset_min >= 0 ? '+' : '−';
  const off = s.at.offset_min ? ` ${sign}${Math.abs(s.at.offset_min)} min` : '';
  return `${t(`groupsPage.at.${s.at.type}`)}${off}`;
}

const DEFAULT_STEPS: api.ProgramStep[] = [
  { at: { type: 'sunset', offset_min: 0 }, level: 100 },
  { at: { type: 'time', time: '23:00' }, level: 60 },
  { at: { type: 'time', time: '05:00' }, level: 100 },
  { at: { type: 'sunrise', offset_min: 0 }, level: 0 },
];

function ProgramModal({
  clientId,
  program,
  onClose,
}: {
  clientId: string;
  program: api.Program | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const readOnly = program !== null && !program.can_manage;
  const [name, setName] = useState(program?.name ?? '');
  const [description, setDescription] = useState(program?.description ?? '');
  const [steps, setSteps] = useState<api.ProgramStep[]>(program?.steps ?? DEFAULT_STEPS);
  const [preview, setPreview] = useState<api.ProgramPreview | null>(null);
  useEffect(() => {
    const id = setTimeout(() => {
      previewProgram(steps, program?.client_id ?? clientId, 1)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(id);
  }, [steps, clientId, program?.client_id]);
  const save = useMutation({
    mutationFn: () =>
      saveProgram(program?.id ?? null, {
        client_id: program ? program.client_id : clientId,
        name,
        description: description || null,
        steps,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['programs'] });
      void qc.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
  });
  const del = useMutation({
    mutationFn: () => deleteProgram(program!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['programs'] });
      onClose();
    },
  });
  const setStep = (i: number, s: api.ProgramStep) => setSteps(steps.map((x, j) => (j === i ? s : x)));
  return (
    <Modal title={program ? program.name : t('groupsPage.newProgram')} onClose={onClose}>
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input
            className="input"
            value={name}
            disabled={readOnly}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </Field>
        <Field label={t('projects.description')}>
          <input
            className="input"
            value={description}
            disabled={readOnly}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </Field>
      </div>
      <h4>{t('groupsPage.steps')}</h4>
      <p className="muted small">{t('groupsPage.stepsHint')}</p>
      {steps.map((s, i) => (
        <div key={i} className="step-row">
          <select
            className="input"
            value={s.at.type}
            disabled={readOnly}
            aria-label={t('groupsPage.when')}
            onChange={(e) => {
              const type = e.target.value as 'time' | 'sunset' | 'sunrise';
              setStep(i, { ...s, at: type === 'time' ? { type, time: '22:00' } : { type, offset_min: 0 } });
            }}
          >
            <option value="time">{t('groupsPage.at.time')}</option>
            <option value="sunset">{t('groupsPage.at.sunset')}</option>
            <option value="sunrise">{t('groupsPage.at.sunrise')}</option>
          </select>
          {s.at.type === 'time' ? (
            <input
              className="input"
              type="time"
              value={s.at.time}
              disabled={readOnly}
              aria-label={t('groupsPage.at.time')}
              onChange={(e) => setStep(i, { ...s, at: { type: 'time', time: e.target.value } })}
            />
          ) : (
            <input
              className="input"
              type="number"
              min={-240}
              max={240}
              value={s.at.offset_min}
              disabled={readOnly}
              aria-label={t('groupsPage.offset')}
              onChange={(e) =>
                setStep(i, {
                  ...s,
                  at: {
                    type: (s.at as { type: 'sunset' | 'sunrise' }).type,
                    offset_min: Number(e.target.value) || 0,
                  },
                })
              }
            />
          )}
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            value={s.level}
            disabled={readOnly}
            aria-label={t('groupsPage.level')}
            onChange={(e) =>
              setStep(i, { ...s, level: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
            }
          />
          {!readOnly && (
            <button
              type="button"
              className="btn btn-ghost"
              aria-label={t('common.remove')}
              onClick={() => setSteps(steps.filter((_, j) => j !== i))}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      {!readOnly && steps.length < 24 && (
        <button
          type="button"
          className="btn"
          onClick={() => setSteps([...steps, { at: { type: 'time', time: '00:00' }, level: 50 }])}
        >
          <Plus size={14} /> {t('groupsPage.addStep')}
        </button>
      )}
      <h4>{t('groupsPage.preview')}</h4>
      {preview && <NightChart night={preview.nights[0]} />}
      {save.isError && (
        <p className="form-error">
          {t(`errors.${(save.error as Error).message}` as 'errors.generic', {
            defaultValue: (save.error as Error).message,
          })}
        </p>
      )}
      <div className="form-actions">
        {program?.can_manage && (
          <button type="button" className="btn btn-danger" onClick={() => del.mutate()}>
            {t('common.delete')}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={onClose}>
          {t('common.close')}
        </button>
        {!readOnly && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim() || !steps.length || save.isPending}
            onClick={() => save.mutate()}
          >
            {t('common.save')}
          </button>
        )}
      </div>
    </Modal>
  );
}

/** Level over the night (18:00 → 08:00 of the next day, local), as a step chart. */
function NightChart({ night }: { night: api.ProgramPreview['nights'][number] | undefined }) {
  const { t } = useTranslation();
  const [ref, width] = useWidth<HTMLDivElement>();
  if (!night) return null;
  const w = Math.max(width, 240);
  const h = 120;
  const start = new Date(`${night.anchor}T12:00:00`).getTime();
  const end = start + 22 * 3_600_000;
  const x = (ms: number) => 30 + ((ms - start) / (end - start)) * (w - 40);
  const y = (lvl: number) => 10 + (1 - lvl / 100) * (h - 30);
  let path = `M${x(start)},${y(0)}`;
  let cur = 0;
  for (const e of night.events) {
    const ms = new Date(e.at).getTime();
    path += ` L${x(ms)},${y(cur)} L${x(ms)},${y(e.level)}`;
    cur = e.level;
  }
  path += ` L${x(end)},${y(cur)}`;
  const hours = [12, 15, 18, 21, 24, 27, 30, 33];
  return (
    <div ref={ref}>
      <svg
        className="chart program-preview"
        width={w}
        height={h}
        role="img"
        aria-label={t('groupsPage.preview')}
      >
        {[0, 50, 100].map((l) => (
          <g key={l}>
            <line className="grid-line" x1={30} x2={w - 10} y1={y(l)} y2={y(l)} />
            <text className="tick" x={26} y={y(l) + 3} textAnchor="end">
              {l}
            </text>
          </g>
        ))}
        <path className="line" d={path} />
        {night.events.map((e, i) => (
          <circle key={i} cx={x(new Date(e.at).getTime())} cy={y(e.level)} r={3} className="bar">
            <title>{`${new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} → ${e.level} %`}</title>
          </circle>
        ))}
        {hours.map((hh) => (
          <text key={hh} className="tick" x={x(start + (hh - 12) * 3_600_000)} y={h - 4} textAnchor="middle">
            {String(hh % 24).padStart(2, '0')}:00
          </text>
        ))}
      </svg>
    </div>
  );
}
